import { FC } from 'react';
import {
  FilterFieldConfig,
  FilterPanelProps,
  FilterFieldType,
} from './filterPanel.types';
import FilterFormRow from '../filterFormRow/filterFormRow';
import DropDownInput from '../dropDownInput/dropDownInput';
import MultiSelectDropdown from '../multiSelectDropdown/multiSelectDropdown';
import DatePickerV3 from '../datePickerV3/datePickerV3';
import TimePickerV3 from '../timePickerV3/timePicker';
import { DateRange } from 'react-day-picker';
import { Dayjs } from 'dayjs';

const renderField = (
  field: FilterFieldConfig,
  values: Record<string, unknown>,
  onChange: (key: string, value: unknown) => void,
): JSX.Element => {
  const singleKey: string = Array.isArray(field.key) ? field.key[0] : field.key;

  switch (field.type) {
    case FilterFieldType.SELECT:
      return (
        <DropDownInput
          className="w-full"
          options={field.options}
          selected={values[singleKey] as string | string[] | undefined}
          onSelect={(value: string | string[]) => onChange(singleKey, value)}
          placeholder={field.placeholder ?? 'Select'}
          search={field.search}
        />
      );

    case FilterFieldType.MULTI_SELECT:
      return (
        <MultiSelectDropdown
          options={field.options}
          selected={values[singleKey] as string | string[] | undefined}
          onSelect={(value: string | string[]) => onChange(singleKey, value)}
          placeholder={field.placeholder ?? 'Select'}
          isMulti
        />
      );

    case FilterFieldType.GROUPED_MULTI_SELECT:
      return (
        <MultiSelectDropdown
          groups={field.groups}
          selected={values[singleKey] as string | string[] | undefined}
          onSelect={(value: string | string[]) => onChange(singleKey, value)}
          placeholder={field.placeholder ?? 'Select'}
          isMulti
          search={field.search}
        />
      );

    case FilterFieldType.TIME_PICKER:
      return (
        <TimePickerV3
          value={(values[singleKey] as Dayjs | null | undefined) ?? null}
          onChange={(value: Dayjs | null) => onChange(singleKey, value)}
        />
      );

    case FilterFieldType.DATE_RANGE: {
      const [fromKey, toKey] = field.key as [string, string];
      return (
        <DatePickerV3
          mode="range"
          value={{
            from: values[fromKey] as Date | undefined,
            to: values[toKey] as Date | undefined,
          }}
          onChange={(range: DateRange) => {
            onChange(fromKey, range.from);
            onChange(toKey, range.to);
          }}
        />
      );
    }

    case FilterFieldType.TIME_RANGE: {
      const [fromKey, toKey] = field.key as [string, string];
      return (
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <TimePickerV3
              value={(values[fromKey] as Dayjs | null | undefined) ?? null}
              onChange={(value: Dayjs | null) => onChange(fromKey, value)}
            />
          </div>
          <span className="text-sm text-gray-500">—</span>
          <div className="flex-1">
            <TimePickerV3
              value={(values[toKey] as Dayjs | null | undefined) ?? null}
              onChange={(value: Dayjs | null) => onChange(toKey, value)}
            />
          </div>
        </div>
      );
    }

    default:
      return <div />;
  }
};

const FilterPanel: FC<FilterPanelProps> = (props) => {
  return (
    <div className="overflow-y-auto max-h-[60vh] px-4">
      {props.fields.map((field: FilterFieldConfig) => {
        const fieldKey: string = Array.isArray(field.key)
          ? field.key.join('-')
          : field.key;

        if (field.type === FilterFieldType.SECTION_HEADER) {
          return (
            <div key={fieldKey} className="mt-6 mb-2">
              {field.showDivider && (
                <hr className="border-t border-gray-200 mb-3" />
              )}
              <span className="text-xs text-gray-500 font-medium">
                {field.label}
              </span>
            </div>
          );
        }

        return (
          <FilterFormRow key={fieldKey} label={field.label}>
            {renderField(field, props.values, props.onChange)}
          </FilterFormRow>
        );
      })}
    </div>
  );
};

export default FilterPanel;
